import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Palette, Users, Award, Calendar, Image, User, MessageCircle, Phone, Mail, X, CalendarDays, MapPin, ArrowRight } from 'lucide-react';
import SEOHead from '../components/SEOHead';
import { trackAdsConversion } from '../utils/analytics';

const API_URL = "";

// Format date as "Month Day, Year"
const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr + 'T00:00:00');
  return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
};

// Format date range for workshops
const formatDateRange = (startDate, endDate) => {
  if (!startDate) return '';
  const start = new Date(startDate + 'T00:00:00');
  const startMonth = start.toLocaleDateString('en-US', { month: 'long' });
  const startDay = start.getDate();
  const startYear = start.getFullYear();
  
  if (!endDate || startDate === endDate) {
    return `${startMonth} ${startDay}, ${startYear}`;
  }
  
  const end = new Date(endDate + 'T00:00:00');
  const endMonth = end.toLocaleDateString('en-US', { month: 'long' });
  const endDay = end.getDate();
  const endYear = end.getFullYear();
  
  if (startMonth === endMonth && startYear === endYear) {
    return `${startMonth} ${startDay}-${endDay}, ${startYear}`;
  } else if (startYear === endYear) {
    return `${startMonth} ${startDay} - ${endMonth} ${endDay}, ${startYear}`;
  }
  return `${startMonth} ${startDay}, ${startYear} - ${endMonth} ${endDay}, ${endYear}`;
};

// Default data for initial render
const defaultAcademyInfo = {
  name: "The Coppini Academy of Fine Art",
  tagline: "Furthering the Interest in Classic, Representational Art Since 1945",
  mission: "The Coppini Academy of Fine Art is dedicated to the promotion and preservation of representational art through educational programs, demonstrations, and workshops."
};

const stripHtml = (html) => {
  if (!html) return '';
  return html.replace(/<[^>]*>/g, '').trim();
};

export const Home = () => {
  const [academyInfo, setAcademyInfo] = useState(defaultAcademyInfo);
  const [programs, setPrograms] = useState([]);
  const [workshops, setWorkshops] = useState([]);
  const [upcomingEventsData, setUpcomingEventsData] = useState([]);
  const [artistOfMonth, setArtistOfMonth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showContactPopup, setShowContactPopup] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [infoRes, programsRes, workshopsRes, artistRes, exhibitionsRes, calendarRes] = await Promise.all([
          fetch(`${API_URL}/api/content/academy-info`),
          fetch(`${API_URL}/api/content/programs`),
          fetch(`${API_URL}/api/content/workshops`),
          fetch(`${API_URL}/api/members/artist-of-month`),
          fetch(`${API_URL}/api/exhibitions/public`),
          fetch(`${API_URL}/api/calendar/events`)
        ]);
        if (infoRes.ok) setAcademyInfo(await infoRes.json());
        if (programsRes.ok) setPrograms(await programsRes.json());
        if (workshopsRes.ok) setWorkshops(await workshopsRes.json());
        if (artistRes.ok) {
          const data = await artistRes.json();
          setArtistOfMonth(data);
        }

        // Build unified upcoming events from Exhibitions + Calendar
        const todayStr = new Date().toISOString().slice(0, 10);
        const merged = [];

        if (exhibitionsRes.ok) {
          const exhibitions = await exhibitionsRes.json();
          for (const ex of exhibitions) {
            if (ex.status === 'active' || ex.status === 'upcoming') {
              merged.push({
                id: ex.id,
                type: 'exhibition',
                title: ex.title,
                sortDate: ex.start_date,
                dateDisplay: formatDateRange(ex.start_date, ex.end_date),
                location: ex.location || '',
                summary: ex.summary || '',
                status: ex.status,
                link: `/exhibitions/${ex.slug}`,
                ctaLabel: 'View Exhibition',
                imageUrl: ex.hero_image_url || '',
              });
            }
          }
        }

        if (calendarRes.ok) {
          const calEvents = await calendarRes.json();
          for (const ev of calEvents) {
            const cat = (ev.category || '').toLowerCase();
            if (ev.date >= todayStr && cat === 'workshop') {
              merged.push({
                id: ev.id,
                type: 'event',
                title: ev.title,
                sortDate: ev.date,
                dateDisplay: formatDate(ev.date),
                location: ev.location || '',
                status: 'upcoming',
                link: '/calendar',
                ctaLabel: 'View Event',
              });
            }
          }
        }

        merged.sort((a, b) => a.sortDate.localeCompare(b.sortDate));
        setUpcomingEventsData(merged.slice(0, 6));
      } catch (err) {
        console.error('Error fetching content:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Filter upcoming workshops (not past, not cancelled) and take first 3
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const upcomingWorkshops = workshops
    .filter(workshop => {
      const workshopDate = new Date(workshop.date + 'T00:00:00');
      const isUpcoming = workshopDate >= today;
      const isActive = workshop.status !== 'cancelled' && workshop.status !== 'rescheduled';
      return isUpcoming && isActive;
    })
    .sort((a, b) => new Date(a.date) - new Date(b.date))
    .slice(0, 3);
    
  const featuredPrograms = programs.slice(0, 3); // eslint-disable-line no-unused-vars

  return (
    <div className="min-h-screen">
      <SEOHead
        title={null}
        description="The Coppini Academy of Fine Art in San Antonio, TX offers classical drawing and painting classes, workshops with master artists, and open studio sessions for adults and youth. Founded in 1945."
        canonicalPath="/"
      />
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        {/* Desktop Hero - with overlay content */}
        <div className="hidden lg:block">
          <div className="absolute inset-0">
            <video 
              autoPlay 
              loop 
              muted 
              playsInline
              className="w-full h-full object-cover"
            >
              <source src="https://customer-assets.emergentagent.com/job_fine-art-academy/artifacts/awuyadp6_WebVideo_CAFA.mp4" type="video/mp4" />
              Your browser does not support the video tag.
            </video>
            <div className="absolute inset-0 bg-gradient-to-r from-stone-900/85 via-stone-900/70 to-stone-900/60"></div>
          </div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-36">
            <div className="max-w-4xl">
              <h1 className="sr-only">The Coppini Academy of Fine Art — Classical Art Education in San Antonio</h1>
              <img 
                src="https://customer-assets.emergentagent.com/job_fine-art-academy-2/artifacts/op3y9zsp_logobanner_poster_white.png"
                alt="The Coppini Academy of Fine Art — Furthering representational art through educational programs, demonstrations, exhibitions and workshops"
                className="w-full max-w-2xl mb-10"
              />
              <p className="text-lg text-stone-200 mb-10 leading-relaxed">
                {academyInfo.mission}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button asChild size="lg" className="bg-white text-stone-900 hover:bg-stone-100">
                  <Link to="/workshops" onClick={trackAdsConversion}>Take a Workshop from a Master Artist</Link>
                </Button>
                <Button asChild size="lg" className="bg-cafa-red hover:bg-cafa-red-900 text-white">
                  <Link to="/classes" onClick={trackAdsConversion}>Explore Classes</Link>
                </Button>
                <Button asChild size="lg" className="bg-white/10 backdrop-blur-sm text-white border border-white/20 hover:bg-white/20">
                  <Link to="/contact">Visit Us</Link>
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile/Tablet Hero - video above, content below */}
        <div className="block lg:hidden">
          {/* Video/Image container with fallback */}
          <div 
            className="relative w-full overflow-hidden"
            style={{ height: '50vh', minHeight: '280px', maxHeight: '400px' }}
          >
            {/* Static image fallback that always shows */}
            <img 
              src="https://customer-assets.emergentagent.com/job_fine-art-academy/artifacts/jo6n3z51_Learning.jpg"
              alt="Art studio at The Coppini Academy"
              className="absolute inset-0 w-full h-full object-cover"
            />
            {/* Video overlay - will play if supported */}
            <video 
              autoPlay 
              loop 
              muted 
              playsInline
              className="absolute inset-0 w-full h-full object-cover"
            >
              <source src="https://customer-assets.emergentagent.com/job_fine-art-academy/artifacts/awuyadp6_WebVideo_CAFA.mp4" type="video/mp4" />
            </video>
          </div>
          
          {/* Content below video */}
          <div className="bg-white px-4 py-8">
            <div className="max-w-2xl mx-auto text-center">
              <p className="text-base text-stone-800 mb-6 leading-relaxed">
                {academyInfo.mission}
              </p>
              <div className="flex flex-col gap-3">
                <Button asChild size="lg" className="bg-stone-900 text-white hover:bg-stone-800">
                  <Link to="/workshops" onClick={trackAdsConversion}>Take a Workshop from a Master Artist</Link>
                </Button>
                <Button asChild size="lg" className="bg-cafa-red hover:bg-cafa-red-900 text-white">
                  <Link to="/classes" onClick={trackAdsConversion}>Explore Classes</Link>
                </Button>
                <Button asChild size="lg" className="bg-stone-200 text-stone-800 hover:bg-stone-300">
                  <Link to="/contact">Visit Us</Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Upcoming Workshops - Immediately after Hero */}
      {upcomingWorkshops.length > 0 && (
        <section className="py-10 bg-white" data-testid="workshops-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-serif text-stone-900 mb-4">Upcoming Workshops</h2>
              <p className="text-stone-600">Learn from master artists in our intensive workshops</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {upcomingWorkshops.map((workshop) => {
                const workshopImage = workshop.instructorImage || 
                  (workshop.images && workshop.images.length > 0 
                    ? (workshop.images[0].startsWith('/api') 
                        ? `${API_URL}${workshop.images[0]}` 
                        : workshop.images[0])
                    : null);
                
                return (
                  <Link 
                    key={workshop.id} 
                    to={`/workshops#workshop-${workshop.id}`}
                    className="group block"
                    data-testid={`workshop-card-${workshop.id}`}
                  >
                    <div className="relative aspect-square rounded-xl overflow-hidden shadow-lg group-hover:shadow-xl transition-shadow">
                      {workshopImage ? (
                        <img 
                          src={workshopImage}
                          alt={workshop.title}
                          className="absolute inset-0 w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                        />
                      ) : (
                        <div className="absolute inset-0 bg-gradient-to-br from-stone-700 to-stone-900" />
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
                      <div className="absolute inset-x-0 bottom-0 flex flex-col items-center text-center p-5 pb-6">
                        <p className="text-white/90 text-base md:text-sm font-medium tracking-wide uppercase leading-tight">
                          {workshop.instructor}
                        </p>
                        <h3 className="text-white text-xl md:text-xl font-serif font-semibold leading-tight mt-1">
                          {workshop.title}
                        </h3>
                        <p className="text-white/80 text-base md:text-sm leading-tight mt-1">
                          {formatDateRange(workshop.date, workshop.endDate)}
                        </p>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
            <div className="text-center mt-10">
              <Button asChild variant="outline" className="border-cafa-red text-cafa-red hover:bg-cafa-red-50">
                <Link to="/workshops">View All Workshops</Link>
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* Upcoming Events - Dynamic from Exhibitions + Calendar */}
      {upcomingEventsData.length > 0 ? (
        <section className="py-12 bg-stone-50" data-testid="upcoming-events-section">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-serif text-stone-900 mb-3">Upcoming Events</h2>
              <p className="text-stone-600">Exhibitions and workshops at the Academy</p>
            </div>
            <div className="space-y-3">
              {upcomingEventsData.map((item) => (
                <Link
                  key={`${item.type}-${item.id}`}
                  to={item.link}
                  className="flex items-center justify-between gap-4 bg-white border border-stone-200 rounded-lg px-5 py-3.5 hover:shadow-sm transition-shadow group"
                  data-testid={`upcoming-event-card-${item.id}`}
                >
                  <div className="flex items-center gap-4 min-w-0">
                    {item.status === 'active' ? (
                      <span className="shrink-0 px-2.5 py-0.5 text-xs font-medium rounded-full bg-green-50 text-green-700 border border-green-200" data-testid="badge-now-showing">Now Showing</span>
                    ) : (
                      <span className="shrink-0 px-2.5 py-0.5 text-xs font-medium rounded-full bg-blue-50 text-blue-700 border border-blue-200" data-testid="badge-upcoming">Upcoming</span>
                    )}
                    <span className="font-serif text-stone-900 truncate">{item.title}</span>
                  </div>
                  <div className="flex items-center gap-4 shrink-0 text-sm text-stone-500">
                    <span className="hidden sm:flex items-center gap-1.5">
                      <CalendarDays size={14} /> {item.dateDisplay}
                    </span>
                    <ArrowRight size={16} className="text-stone-400 group-hover:text-cafa-red transition-colors" />
                  </div>
                </Link>
              ))}
            </div>
            <div className="text-center mt-8">
              <Button asChild variant="outline" className="border-cafa-red text-cafa-red hover:bg-cafa-red-50">
                <Link to="/calendar">View All Events</Link>
              </Button>
            </div>
          </div>
        </section>
      ) : null}

      {/* Features Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-cafa-red/90 rounded-2xl p-6 text-center">
              <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Palette className="text-white" size={28} />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Classical Training</h3>
              <p className="text-sm text-white/80">
                Master traditional techniques in drawing, painting, and sculpture
              </p>
            </div>

            <div className="bg-cafa-red/90 rounded-2xl p-6 text-center">
              <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="text-white" size={28} />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Expert Instructors</h3>
              <p className="text-sm text-white/80">
                Learn from accomplished artists with years of teaching experience
              </p>
            </div>

            <div className="bg-cafa-red/90 rounded-2xl p-6 text-center">
              <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Award className="text-white" size={28} />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">80+ Years Legacy</h3>
              <p className="text-sm text-white/80">
                Trusted fine art education since 1945
              </p>
            </div>

            <div className="bg-cafa-red/90 rounded-2xl p-6 text-center">
              <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Calendar className="text-white" size={28} />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Flexible Schedule</h3>
              <p className="text-sm text-white/80">
                Classes, workshops, and open studio sessions for all levels
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Visual Showcase Section */}
      <section className="py-16 bg-stone-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center mb-16">
            <div>
              <h2 className="text-3xl font-serif text-stone-900 mb-4">Classical Techniques, Timeless Art</h2>
              <p className="text-stone-600 leading-relaxed mb-6">
                Our curriculum emphasizes traditional methods passed down through generations. From figure drawing to oil painting, 
                students learn the foundational skills that define classical representational art.
              </p>
              <p className="text-stone-600 leading-relaxed">
                Work alongside experienced instructors in our studio spaces, where every session is an opportunity to 
                refine your technique and deepen your understanding of the visual arts.
              </p>
            </div>
            <div className="relative h-96 rounded-lg overflow-hidden shadow-xl">
              <img 
                src="https://images.unsplash.com/photo-1547930684-0827d6426281" 
                alt="Classical figure drawing" 
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div className="relative h-96 rounded-lg overflow-hidden shadow-xl order-2 md:order-1">
              <img 
                src="https://customer-assets.emergentagent.com/job_artsite-debug/artifacts/kb15jgwo_kraigclass.jpg" 
                alt="Students learning at The Coppini Academy" 
                className="w-full h-full object-cover"
              />
            </div>
            <div className="order-1 md:order-2">
              <h2 className="text-3xl font-serif text-stone-900 mb-4">Master Traditional Media</h2>
              <p className="text-stone-600 leading-relaxed mb-6">
                Study the techniques of the old masters through oil painting, watercolor, and classical drawing methods. 
                Our instructors guide you through color theory, composition, and the technical skills needed to bring your vision to life.
              </p>
              <p className="text-stone-600 leading-relaxed">
                Whether you are capturing the delicate transparency of watercolor or the rich depth of oil painting, 
                our classes provide the structure and guidance to develop your artistic voice.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Kids Classes Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div className="relative h-96 rounded-lg overflow-hidden shadow-xl">
              <img
                src="https://customer-assets.emergentagent.com/job_13381205-307a-4c7d-8288-c75d39e5acae/artifacts/w88agz7d_530730657_18481418509078562_8302995277017080765_n.jpg"
                alt="Children creating art at The Coppini Academy studio"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div>
              <span className="inline-block px-4 py-1.5 bg-cafa-red-100 text-cafa-red-900 text-sm font-medium rounded-full mb-4">
                Ages 5–18
              </span>
              <h2 className="text-3xl font-serif text-stone-900 mb-4">Kids Classes</h2>
              <p className="text-stone-600 leading-relaxed mb-6">
                Give your child the gift of creative expression. Our after-school art programs teach drawing, painting, and sculpture fundamentals in a welcoming studio environment — guided by experienced instructors who nurture young artists at every level.
              </p>
              <p className="text-stone-600 leading-relaxed mb-8">
                Small class sizes ensure personalized instruction, helping each student build confidence and develop real skills they'll carry for a lifetime.
              </p>
              <Link to="/classes/kids">
                <Button className="bg-cafa-red hover:bg-cafa-red-800 text-white px-8 py-3 text-base" data-testid="kids-classes-cta">
                  Explore Kids Classes <ArrowRight className="ml-2" size={18} />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Artist Member of the Month Section */}
      {artistOfMonth?.artist && (
        <section className="py-20 bg-gradient-to-b from-stone-50 to-white" data-testid="artist-of-month-section">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            {(() => {
              const artist = artistOfMonth.artist;
              return (
                <>
                  {/* Section Title */}
                  <div className="text-center mb-6">
                    <span className="inline-block px-4 py-1.5 bg-cafa-red-100 text-cafa-red-900 text-sm font-medium rounded-full mb-4">
                      Featured Artist
                    </span>
                    <h2 className="text-3xl md:text-4xl font-serif text-stone-900">
                      {artistOfMonth.custom_title || "Artist Member of the Month"}
                    </h2>
                  </div>

                  {/* Artist Profile - Centered at Top */}
                  <div className="text-center mb-12">
                    {/* Large Profile Photo */}
                    <div className="relative inline-block mb-6">
                      {artist.photo_url ? (
                        <img
                          src={artist.photo_url.startsWith('http') ? artist.photo_url : `${API_URL}${artist.photo_url}`}
                          alt={`${artist.first_name} ${artist.last_name}`}
                          className="w-36 h-36 md:w-44 md:h-44 rounded-full object-cover ring-4 ring-white shadow-xl"
                        />
                      ) : (
                        <div className="w-36 h-36 md:w-44 md:h-44 rounded-full bg-gradient-to-br from-cafa-red-100 to-cafa-red-200 flex items-center justify-center ring-4 ring-white shadow-xl">
                          <User size={64} className="text-cafa-red" />
                        </div>
                      )}
                      {/* Decorative accent */}
                      <div className="absolute -bottom-2 -right-2 w-12 h-12 bg-cafa-red rounded-full flex items-center justify-center shadow-lg">
                        <span className="text-white text-xl">★</span>
                      </div>
                    </div>

                    {/* Artist Name */}
                    <h3 className="text-3xl md:text-4xl font-serif font-semibold text-stone-900 mb-2">
                      {artist.first_name} {artist.last_name}
                    </h3>

                    {/* Location & Badge */}
                    <div className="flex items-center justify-center gap-3 flex-wrap">
                      {(artist.city || artist.state) && (
                        <p className="text-stone-500 text-lg">
                          {[artist.city, artist.state].filter(Boolean).join(', ')}
                        </p>
                      )}
                      {artist.membership_type && (
                        <span className="px-4 py-1.5 bg-stone-100 text-stone-700 text-sm font-medium rounded-full capitalize">
                          {artist.membership_type} Member
                        </span>
                      )}
                    </div>

                    {/* Optional Bio */}
                    {artist.bio && (
                      <p className="text-stone-600 mt-4 max-w-2xl mx-auto leading-relaxed">
                        {artist.bio}
                      </p>
                    )}
                  </div>

                  {/* Gallery Section - Only show if there are images on persistent storage */}
                  {(() => {
                    const cloudinaryImages = (artist.gallery_preview || []).filter(img => {
                      const url = img.url || '';
                      const isDeadCloudinary = url.includes('res.cloudinary.com') || url.includes('/image/upload/');
                      return url && !isDeadCloudinary;
                    });
                    
                    if (cloudinaryImages.length === 0) {
                      return null; // Don't show gallery section if no valid images
                    }
                    
                    return (
                      <div className="space-y-4">
                        {/* Gallery Header */}
                        <div className="flex items-center justify-center gap-3 mb-6">
                          <div className="h-px bg-stone-300 flex-1 max-w-[100px]"></div>
                          <div className="flex items-center gap-2 text-stone-600">
                            <Image size={20} className="text-cafa-red" />
                            <span className="font-medium">{cloudinaryImages.length} Artwork{cloudinaryImages.length !== 1 ? 's' : ''}</span>
                          </div>
                          <div className="h-px bg-stone-300 flex-1 max-w-[100px]"></div>
                        </div>

                        {/* Gallery Grid */}
                        <div className={`grid gap-4 ${
                          cloudinaryImages.length === 1 ? 'grid-cols-1 max-w-2xl mx-auto' :
                          cloudinaryImages.length === 2 ? 'grid-cols-2 max-w-3xl mx-auto' :
                          cloudinaryImages.length === 3 ? 'grid-cols-3 max-w-4xl mx-auto' :
                          'grid-cols-2 md:grid-cols-4'
                        }`}>
                          {cloudinaryImages.slice(0, 4).map((img, idx) => (
                            <div 
                              key={img.id || idx}
                              className="group relative aspect-square rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 bg-stone-100"
                            >
                              <img
                                src={img.url}
                                alt={img.title || 'Artwork'}
                                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.parentElement.classList.add('hidden');
                                }}
                              />
                              {/* Hover Overlay */}
                              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-4">
                                {img.title && (
                                  <p className="text-white font-medium text-lg">{img.title}</p>
                                )}
                                {img.medium && (
                                  <p className="text-white/80 text-sm">{img.medium}{img.year ? `, ${img.year}` : ''}</p>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })()}

                  {/* CTA Button */}
                  <div className="text-center mt-10">
                    <Button asChild className="bg-cafa-red hover:bg-cafa-red-900 text-white px-8 py-3 text-lg rounded-full shadow-lg hover:shadow-xl transition-all">
                      <Link to="/members/directory">Explore Member Directory</Link>
                    </Button>
                  </div>
                </>
              );
            })()}
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-20 bg-cafa-red">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-serif text-white mb-6">
            Begin Your Artistic Journey Today
          </h2>
          <p className="text-lg text-stone-100 mb-8">
            Join our community of artists and discover the joy of classical fine art education.
          </p>
          <Button asChild size="lg" className="bg-white text-cafa-red hover:bg-cafa-red-50">
            <Link to="/membership" onClick={trackAdsConversion}>Become a Member</Link>
          </Button>
        </div>
      </section>

      {/* Questions? Floating Popup */}
      <div className="fixed bottom-6 right-6 z-50">
        {showContactPopup ? (
          <div className="bg-white rounded-xl shadow-2xl p-4 mb-2 w-64 border border-stone-200 animate-in fade-in slide-in-from-bottom-2 duration-200">
            <div className="flex justify-between items-center mb-3">
              <h3 className="font-semibold text-stone-900">Questions?</h3>
              <button 
                onClick={() => setShowContactPopup(false)}
                className="text-stone-400 hover:text-stone-600 transition-colors"
              >
                <X size={18} />
              </button>
            </div>
            <p className="text-sm text-stone-600 mb-4">We're here to help!</p>
            <div className="space-y-2">
              <a 
                href="tel:210-530-8445" 
                className="flex items-center gap-3 p-2 rounded-lg hover:bg-stone-50 transition-colors text-stone-700"
              >
                <div className="w-8 h-8 bg-cafa-red/10 rounded-full flex items-center justify-center">
                  <Phone size={16} className="text-cafa-red" />
                </div>
                <span className="text-sm font-medium">(210) 530-8445</span>
              </a>
              <a 
                href="mailto:info@thecoppini.org" 
                className="flex items-center gap-3 p-2 rounded-lg hover:bg-stone-50 transition-colors text-stone-700"
              >
                <div className="w-8 h-8 bg-cafa-red/10 rounded-full flex items-center justify-center">
                  <Mail size={16} className="text-cafa-red" />
                </div>
                <span className="text-sm font-medium">Email Us</span>
              </a>
            </div>
          </div>
        ) : null}
        <button
          onClick={() => setShowContactPopup(!showContactPopup)}
          className="bg-cafa-red hover:bg-cafa-red-900 text-white rounded-full px-5 py-3 shadow-lg flex items-center gap-2 transition-all hover:scale-105"
        >
          <MessageCircle size={20} />
          <span className="font-medium">Questions?</span>
        </button>
      </div>
    </div>
  );
};
